// @flow strict
import * as React from 'react';
import styledImport from 'styled-components';
const styled = styledImport.default || styledImport;
import { Box, Flex } from '../../primitives/Essentials/index.js';
import IntlContext from '../../services/intl/context.js';
import MagazineContext from '../../services/magazine/context.js';
import type { MagazinePostSummary } from '../../services/magazine/context.js';
import { container } from '../../services/styleUtils/index.js';
import Footer from '../../components/Footer/index.js';
import MoreLinks from '../../components/MoreLinks/index.js';
import Navbar from '../../components/Navbar/index.js';
import Breadcrumbs from '../../components/Breadcrumbs/index.js';
import { SECTION_LAYOUT } from '../../consts/layout.js';

const INTRO = {
  en: 'Travel research, ticket comparisons, destination ideas, and practical planning notes from TicketLens.',
  de: 'Reiserecherche, Ticketvergleiche, Ideen fuer Reiseziele und praktische Planungshinweise von TicketLens.',
  it: 'Ricerche di viaggio, confronti sui biglietti, idee per destinazioni e consigli pratici da TicketLens.',
};

const LATEST = {
  en: 'Latest Articles',
  de: 'Neue Artikel',
  it: 'Nuovi Articoli',
};

const StyledContainer = styled.div`
  position: relative;
`;

const StyledContainerBox = styled(Box)`
  ${container()};
`;

const Hero = styled.section`
  padding: 58px 15px 50px;
  color: #fff;
  background:
    linear-gradient(120deg, rgba(0, 0, 0, 0.72), rgba(197, 17, 98, 0.68)),
    url('https://cdn.ticketlens.com/magazine/imgs/2019/04/1400x800.jpeg') center/cover;
`;

const HeroInner = styled.div`
  ${container()};
`;

const Eyebrow = styled.div`
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
`;

const Title = styled.h1`
  max-width: 820px;
  margin: 0;
  font-size: 44px;
  font-weight: 900;
  line-height: 1.08;
`;

const Intro = styled.p`
  max-width: 720px;
  margin: 18px 0 0;
  font-size: 19px;
  line-height: 1.5;
`;

const SectionTitle = styled.h2`
  margin: 0 0 24px;
  color: ${({ theme }) => theme.section.title};
  font-size: 28px;
  font-weight: 900;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.a`
  display: flex;
  min-width: 0;
  height: 100%;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e6e2e4;
  border-radius: 8px;
  color: inherit;
  background: #fff;
  text-decoration: none;
  transition: border-color 140ms ease, transform 140ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.secondary};
    transform: translateY(-2px);
  }
`;

const CardImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  background: #eee;
`;

const CardBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 18px;
`;

const Meta = styled.div`
  color: ${({ theme }) => theme.secondary};
  font-size: 13px;
  font-weight: 900;
`;

const CardTitle = styled.h3`
  margin: 10px 0 10px;
  color: ${({ theme }) => theme.section.title};
  font-size: 20px;
  font-weight: 900;
  line-height: 1.25;
`;

const Excerpt = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.section.abstract};
  font-size: 15px;
  line-height: 1.45;
`;

const formatDate = (language, date) =>
  date
    ? new Intl.DateTimeFormat(language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(new Date(date))
    : '';

const categoryLabel = post =>
  post.categories && post.categories[0] ? post.categories[0].name : 'Magazine';

const MagazineCard = ({ post, language }: { post: MagazinePostSummary, language: string }) => (
  <Card href={post.urlPath}>
    {post.featuredImage && (
      <CardImage src={post.featuredImage.url} alt={post.featuredImage.alt || post.title} />
    )}
    <CardBody>
      <Meta>
        {categoryLabel(post)}
        {post.date ? ` | ${formatDate(language, post.date)}` : ''}
      </Meta>
      <CardTitle>{post.title}</CardTitle>
      <Excerpt>{post.excerpt}</Excerpt>
    </CardBody>
  </Card>
);

const MagazineIndexPage = () => {
  const { language, translate } = React.useContext(IntlContext);
  const { posts } = React.useContext(MagazineContext);

  return (
    <>
      <StyledContainer>
        <Navbar showOptions={false} />
      </StyledContainer>
      <Hero>
        <HeroInner>
          <Eyebrow>TicketLens</Eyebrow>
          <Title>{translate('magazine')}</Title>
          <Intro>{INTRO[language] || INTRO.en}</Intro>
        </HeroInner>
      </Hero>
      <StyledContainerBox {...SECTION_LAYOUT} $mt={0}>
        <Flex $flexDirection="column">
          <Breadcrumbs
            items={[
              {
                name: translate('magazine'),
                url: `/${language}/magazine`,
              },
            ]}
            activeIndex={0}
          />
          <SectionTitle>{LATEST[language] || LATEST.en}</SectionTitle>
          <Grid>
            {posts.map(post => (
              <MagazineCard key={post.id} post={post} language={language} />
            ))}
          </Grid>
        </Flex>
      </StyledContainerBox>
      <MoreLinks attractionsLimit={10} destinationsLimit={12} hideTopics />
      <Footer showOptions={false} />
    </>
  );
};

export default MagazineIndexPage;
